import { doc, getDoc } from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import { NLS_HELPER_APP_LOCALES, NLS_LOCALE_LABELS, getNlsTypography, normalizeHelperAppLocale } from '../../nls/nlsRuntime'
import type { NlsLocale } from '../../nls/nlsTypes'

export { normalizeHelperAppLocale } from '../../nls/nlsRuntime'

export type SupportedLanguage = {
  code: string
  label: string
  nativeLabel: string
  rtl: boolean
}

export type LanguageRegistry = {
  version: number
  supportedLanguages: SupportedLanguage[]
  translateCreditCost: number
  summaryCreditCost: number
  defaultLanguageByRegion: Record<string, string>
}

const LANGUAGE_STORAGE_KEY = 'helper.languagePreference'
export const HELPER_APP_LOCALE_STORAGE_KEY = 'helper.appLocalePreference'
const DEFAULT_LANGUAGE = 'en'
export const DEFAULT_HELPER_APP_LOCALE: NlsLocale = 'en'

export type HelperAppLocaleOption = {
  code: NlsLocale
  label: string
  nativeLabel: string
  rtl: boolean
}

export const HELPER_APP_LOCALE_OPTIONS: HelperAppLocaleOption[] = NLS_HELPER_APP_LOCALES.map((code) => ({
  code,
  label: NLS_LOCALE_LABELS[code].label,
  nativeLabel: NLS_LOCALE_LABELS[code].nativeLabel,
  rtl: getNlsTypography(code).dir === 'rtl',
}))

const FALLBACK_SUPPORTED_LANGUAGES: SupportedLanguage[] = HELPER_APP_LOCALE_OPTIONS.map((entry) => ({
  code: entry.code,
  label: entry.label,
  nativeLabel: entry.nativeLabel,
  rtl: entry.rtl,
}))

const FALLBACK_REGISTRY: LanguageRegistry = {
  version: 1,
  supportedLanguages: FALLBACK_SUPPORTED_LANGUAGES,
  translateCreditCost: 1,
  summaryCreditCost: 2,
  defaultLanguageByRegion: {
    IN: 'hi',
    US: 'en',
    GB: 'en',
    AE: 'ar',
    PK: 'ur',
    IR: 'fa',
    RU: 'ru',
    JP: 'ja',
    DE: 'de',
    KR: 'ko',
    TR: 'tr',
    VN: 'vi',
    IT: 'it',
    PL: 'pl',
    UA: 'uk',
    NL: 'nl',
    TH: 'th',
    ID: 'id',
    MY: 'ms',
    PH: 'fil',
    BR: 'pt',
    PT: 'pt',
    MX: 'es',
    FR: 'fr',
  },
}

let cachedRegistry: LanguageRegistry | null = null
let cacheExpiresAt = 0
const CACHE_TTL_MS = 5 * 60 * 1000

const normalizeRegistry = (raw: unknown): LanguageRegistry => {
  if (!raw || typeof raw !== 'object') return FALLBACK_REGISTRY
  const data = raw as Record<string, unknown>
  const languages = Array.isArray(data.supportedLanguages)
    ? (data.supportedLanguages as Array<Record<string, unknown>>)
        .filter((entry) => typeof entry.code === 'string' && typeof entry.label === 'string')
        .map((entry) => ({
          code: String(entry.code),
          label: String(entry.label),
          nativeLabel: typeof entry.nativeLabel === 'string' ? String(entry.nativeLabel) : String(entry.label),
          rtl: entry.rtl === true,
        }))
    : FALLBACK_REGISTRY.supportedLanguages
  const region = data.defaultLanguageByRegion && typeof data.defaultLanguageByRegion === 'object'
    ? Object.fromEntries(
        Object.entries(data.defaultLanguageByRegion as Record<string, unknown>)
          .filter(([, value]) => typeof value === 'string') as Array<[string, string]>,
      )
    : FALLBACK_REGISTRY.defaultLanguageByRegion
  return {
    version: typeof data.version === 'number' ? data.version : FALLBACK_REGISTRY.version,
    supportedLanguages: languages,
    translateCreditCost: typeof data.translateCreditCost === 'number'
      ? data.translateCreditCost
      : FALLBACK_REGISTRY.translateCreditCost,
    summaryCreditCost: typeof data.summaryCreditCost === 'number'
      ? data.summaryCreditCost
      : FALLBACK_REGISTRY.summaryCreditCost,
    defaultLanguageByRegion: region,
  }
}

export const fetchLanguageRegistry = async (): Promise<LanguageRegistry> => {
  if (cachedRegistry && Date.now() < cacheExpiresAt) return cachedRegistry
  try {
    const snapshot = await getDoc(doc(firestoreDb, 'config', 'i18n'))
    const registry = snapshot.exists() ? normalizeRegistry(snapshot.data()) : FALLBACK_REGISTRY
    cachedRegistry = registry
    cacheExpiresAt = Date.now() + CACHE_TTL_MS
    return registry
  } catch {
    cachedRegistry = FALLBACK_REGISTRY
    cacheExpiresAt = Date.now() + CACHE_TTL_MS
    return FALLBACK_REGISTRY
  }
}

export const getHelperLanguagePreference = (): string => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? DEFAULT_LANGUAGE
  } catch {
    return DEFAULT_LANGUAGE
  }
}

export const getHelperNlsLocalePreference = (): NlsLocale =>
  getHelperAppLocalePreference()

export const getHelperAppLocalePreference = (): NlsLocale => {
  if (typeof window === 'undefined') return DEFAULT_HELPER_APP_LOCALE
  try {
    return normalizeHelperAppLocale(window.localStorage.getItem(HELPER_APP_LOCALE_STORAGE_KEY))
  } catch {
    return DEFAULT_HELPER_APP_LOCALE
  }
}

export const hasHelperAppLocalePreference = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const value = window.localStorage.getItem(HELPER_APP_LOCALE_STORAGE_KEY)
    return typeof value === 'string' && value.trim().length > 0
  } catch {
    return false
  }
}

export const setHelperAppLocalePreference = (code: string): NlsLocale => {
  const locale = normalizeHelperAppLocale(code)
  if (typeof window === 'undefined') return locale
  try {
    window.localStorage.setItem(HELPER_APP_LOCALE_STORAGE_KEY, locale)
  } catch {
    // ignore quota / privacy errors
  }
  return locale
}

export const setHelperLanguagePreference = (code: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code)
  } catch {
    // ignore quota / privacy errors
  }
}

export const resetLanguageRegistryCache = (): void => {
  cachedRegistry = null
  cacheExpiresAt = 0
}
